import { Box, Button, Chip, Divider, Grid, IconButton, Stack, TextField, Typography } from "@mui/material";
import { BasePathProvider, useBasePath } from "components/Providers";
import DOMPurify from 'dompurify';
import * as yup from 'yup'
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { save, search } from "store/academic/parent/esign";
import { useDispatch } from "react-redux";
import { CloseIcon } from "components/Icons";
import { FileUploadField } from "components/FileUploadField";

export default function Form({ activeDocument, studentName, memberId, handleCloseDrawer, activeLanguage }) {
  const basePath = useBasePath();
  const { t } = useTranslation();
  const sanitizedHtml = DOMPurify.sanitize(activeDocument.template);
  const initialResponse = (activeDocument?.formResponses?.[0]?.response ?? '');
  const isSigned = !!activeDocument?.formResponses?.[0]?.id;
  const [selectedOption, setSelectedOption] = useState(initialResponse)
  const [uploadedFile, setUploadedFile] = useState(null)
  const dispatch = useDispatch();
  const isFileUpload = activeDocument?.links?.fileUpload

  const schema = yup.object().shape({
    formResponses: yup.array().of(
      yup.object().shape({
        signature: yup.string().required('Name of person filling out the form is required'),
      })
    ),
    response: yup.string().required('Response is required'),
  });

  const { control, handleSubmit, formState, register, setValue } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ...activeDocument,
      formResponses: activeDocument.formResponses?.length
        ? activeDocument.formResponses
        : [{ signature: '' }],
      formId: activeDocument.id,
      date: dayjs(),
      response: initialResponse || ''
    }
  })

  const onSubmit = async (data) => {
    const formResponse = {
      signature: data.formResponses[0].signature,
      formId: activeDocument.id,
      response: selectedOption
    };

    const res = await dispatch(save({ memberId, ...formResponse }));

    if (res.payload) {
      await dispatch(search({ memberId }));
      handleCloseDrawer();
    }
  };

  const htmlRef = useRef();

  useEffect(() => {
    const container = htmlRef.current;
    if (!container) return;

    const radios = container.querySelectorAll('input[type="radio"]');

    radios.forEach((radio) => {
      radio.checked = radio.value === String(selectedOption);
    });
  }, [selectedOption, sanitizedHtml]);

  useEffect(() => {
    const container = htmlRef.current;
    if (!container) return;

    container.querySelectorAll('input, select, textarea, button').forEach((field) => {
      field.disabled = isSigned;
    });
  }, [isSigned, sanitizedHtml]);

  useEffect(() => {
    const container = htmlRef.current;
    if (!container) return;

    container.querySelectorAll('a[href]').forEach((link) => {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    });
  }, [sanitizedHtml]);

  // TODO: upload to /api/file and attach the id to the form response on submit
  const handleFileChange = (file) => {
    setUploadedFile(file);
    console.log('[Esign] file selected:', file);
  };

  const handleFileError = (message) => {
    if (message) console.log('[Esign] file rejected:', message);
  };

  const handleRadioChange = (e) => {
    if (isSigned) return;
    const value = e.target.value;
    setSelectedOption(value);
    setValue('response', value, { shouldValidate: true });
  };

  useEffect(() => {
    const container = htmlRef.current;
    if (!container) return;

    container.addEventListener('change', handleRadioChange);

    return () => {
      container.removeEventListener('change', handleRadioChange);
    };
  }, []);

  return (
    <BasePathProvider value={`${basePath}`}>
      <Box data-component='esignForm' component="form" onSubmit={handleSubmit(onSubmit)}>
        <Stack sx={{ displayPrint: 'none', width: '100%' }} direction="row" justifyContent="space-between" alignItems="center" spacing={2} px={2} pt={2} pb={1}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 400 }} noWrap>{t(activeDocument.title)}</Typography>
            {isSigned && <Chip size="small" color="success" label={t('Signed')} sx={{ flexShrink: 0 }} />}
          </Stack>
          <IconButton aria-label="close" onClick={() => handleCloseDrawer()}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Divider />
        <Box>
          <Box
            px={2}
            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            data-lang={activeLanguage}
            ref={htmlRef}
          />
          <input type="hidden" {...register('response')} />
          {formState.errors.response && (
            <Typography variant="caption" color="error" sx={{ px: 2 }}>
              {formState.errors.response.message}
            </Typography>
          )}
        </Box>
        {isFileUpload && (
          <Box px={2} pt={1}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('Attachment')}
            </Typography>
            <Box sx={{ pointerEvents: isSigned ? 'none' : 'auto', opacity: isSigned ? 0.6 : 1 }}>
              <FileUploadField
                value={uploadedFile}
                onChange={handleFileChange}
                onError={handleFileError}
                label={t('Click to upload or drag and drop')}
              />
            </Box>
          </Box>
        )}
        <Grid container spacing={2} sx={{ padding: '20px' }}>
          <Grid item xs={12} sm={4}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Student Name')}
                  variant="outlined"
                  disabled
                  size="small"
                  defaultValue={studentName}
                  value={studentName}
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('Sign Date')}
                  variant="outlined"
                  disabled
                  size="small"
                  defaultValue={dayjs().format('MM/DD/YYYY')}
                  value={dayjs().format('MM/DD/YYYY')}
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller
              name="formResponses[0].signature"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={t('Name of Person Filling Out Form')}
                  variant="outlined"
                  size="small"
                  fullWidth
                  disabled={isSigned}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
        </Grid>
        {!isSigned && (
          <Box display="flex" justifyContent="flex-end" pr={2} pb={2}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!formState.isDirty || formState.isSubmitting}
            >
              {t('Submit')}
            </Button>
          </Box>
        )}
      </Box>
    </BasePathProvider>
  );
}
